import { CheckInGradientCircle } from "assets/icons";
import cn from "classnames";
import { useBoolean, useOnClickOutside } from "hooks";
import { t } from "i18next";
import { useRefetchQuery } from "libs/reactQuery";
import { useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import { Modal } from "ui";

import { useChangeResellerLevel } from "../../apis";
import { ResellerLevel } from "../../constants";
import { getResellerLevelLabelText } from "../../helpers";
import { ResellerLevelLabel } from "../ResellerLevelLabel";
import { ResellerLevelControlProps } from "./ResellerLevelControl.types";

import styles from "./ResellerLevelControl.module.scss";

const ResellerLevelControl = ({
  resellerId,
  currentLevel,
  isDisabled
}: ResellerLevelControlProps) => {
  const {
    value: isDropdownOpen,
    toggle: toggleDropdown,
    setFalse: closeDropdown
  } = useBoolean(false);

  const [newLevelForConfirm, setNewLevelForConfirm] =
    useState<ResellerLevel | null>(null);

  const selectorRef = useRef(null);

  const refetchReseller = useRefetchQuery(["reseller", resellerId]);

  const changeResellerLevel = useChangeResellerLevel({
    onSuccess: () => {
      setNewLevelForConfirm(null);
      closeDropdown();
      refetchReseller();
      toast.success(t("User level has been changed successfully"));
    }
  });

  const options = useMemo(() => {
    return [
      {
        label: "Buyer",
        value: ResellerLevel.Buyer
      },
      {
        label: "Reseller",
        value: ResellerLevel.IncompleteReseller
      },
      {
        label: "Beginner Reseller",
        value: ResellerLevel.BeginnerReseller
      },
      {
        label: "Intermediate Reseller",
        value: ResellerLevel.IntermediateReseller
      },
      {
        label: "Confirmed Reseller",
        value: ResellerLevel.ConfirmedReseller
      },
      {
        label: "Influencer",
        value: ResellerLevel.Influencer
      }
    ];
  }, []);

  const currentOptionIndex = useMemo(() => {
    return options.findIndex((option) => option.value === currentLevel);
  }, [currentLevel, options]);

  useOnClickOutside(selectorRef, closeDropdown);

  return (
    <div ref={selectorRef} className={styles.container}>
      <button
        className={styles.selector}
        disabled={currentLevel === ResellerLevel.Influencer || isDisabled}
        onClick={toggleDropdown}
      >
        <ResellerLevelLabel
          level={currentLevel}
          withDropdownArrow={
            currentLevel !== ResellerLevel.Influencer && !isDisabled
          }
        />
      </button>

      {isDropdownOpen && (
        <div className={styles.selector_dropdown}>
          <span className={styles.selector_dropdown_title}>
            {t("Change user level")}:
          </span>

          {options.map((option, index) => {
            const isSelected = currentOptionIndex === index;

            const isOptionDisabled = currentOptionIndex >= index;

            return (
              <button
                key={option.value}
                className={cn(styles.selector_dropdown_option, {
                  [styles.selector_dropdown_option_disabled]: isOptionDisabled
                })}
                onClick={() => {
                  setNewLevelForConfirm(option.value);
                }}
                disabled={isOptionDisabled}
              >
                <ResellerLevelLabel level={option.value} />

                {isSelected && <CheckInGradientCircle />}
              </button>
            );
          })}
        </div>
      )}

      {newLevelForConfirm && (
        <Modal
          title={t("Change user level?")}
          isOpen={!!newLevelForConfirm}
          className={styles.modal}
          contentClassName={styles.modal_content}
          okText="Confirm"
          onOk={() => {
            changeResellerLevel.mutate({
              resellerId,
              newLevel: newLevelForConfirm
            });
          }}
          isLoading={changeResellerLevel.isLoading}
          onClose={() => {
            setNewLevelForConfirm(null);
          }}
        >
          <p>
            {t("User level will be upgraded to")}{" "}
            <span className={styles.modal_boldText}>
              {t(getResellerLevelLabelText(newLevelForConfirm))}
            </span>
            .
          </p>
        </Modal>
      )}
    </div>
  );
};

export default ResellerLevelControl;
